import React, { useMemo, useState } from 'react';
import { Link, Navigate, useNavigate } from 'react-router-dom';
import { httpsCallable } from 'firebase/functions';
import SeoHead from '../components/SeoHead';
import { useAuth } from '../context/AuthContext';
import { functions } from '../firebase/config';
import './AccountPage.css';

export default function AccountPage() {
  const { user, profile, loading, logout } = useAuth();
  const navigate = useNavigate();

  const [confirmChecked, setConfirmChecked] = useState(false);
  const [confirmText, setConfirmText] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [done, setDone] = useState(false);

  const displayName = profile?.displayName || user?.displayName || 'Player';
  const email = user?.email || null;

  const canDelete = confirmChecked && confirmText.trim().toUpperCase() === 'DELETE' && !busy;

  const callable = useMemo(() => httpsCallable(functions, 'deleteAccount'), []);

  if (loading) return null;
  if (!user) return <Navigate to="/login" replace />;

  const doDelete = async () => {
    const ok = window.confirm(
      'Delete your account and associated data? This is permanent and cannot be undone.'
    );
    if (!ok) return;

    setError(null);
    setBusy(true);
    try {
      await callable({});
      setDone(true);
      try {
        await logout();
      } catch {
        // ignore: user is already deleted server-side
      }
      navigate('/', { replace: true });
    } catch (e) {
      setError('Failed to delete account. Please try again.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <main className="account-page">
      <SeoHead title="Account" description="Your Letter Quest account settings." path="/account" />

      <h1>Account</h1>

      <section className="account-page__card" aria-label="Profile">
        <h2>Profile</h2>
        <dl className="account-page__dl">
          <div>
            <dt>Display name</dt>
            <dd>{displayName}</dd>
          </div>
          <div>
            <dt>Email</dt>
            <dd>{email || '—'}</dd>
          </div>
        </dl>
        <p className="account-page__note">
          Need help? See <Link to="/account-deletion">Account deletion</Link>, <Link to="/privacy">Privacy</Link>, and{' '}
          <Link to="/terms">Terms</Link>.
        </p>
      </section>

      <section className="account-page__danger" aria-label="Delete account">
        <h2>Delete account</h2>
        <p>
          This is permanent. We’ll delete your account and associated gameplay data. Public leaderboards will no longer
          show your name after deletion.
        </p>

        <label className="account-page__check">
          <input
            type="checkbox"
            checked={confirmChecked}
            onChange={(e) => setConfirmChecked(e.target.checked)}
            disabled={busy}
          />
          I understand this cannot be undone.
        </label>

        <label className="account-page__label">
          Type <strong>DELETE</strong> to confirm
          <input
            className="account-page__input"
            value={confirmText}
            onChange={(e) => setConfirmText(e.target.value)}
            disabled={busy}
            autoComplete="off"
            inputMode="text"
          />
        </label>

        {error && (
          <div className="account-page__error" role="alert">
            {error}
          </div>
        )}

        {done && (
          <div className="account-page__success" role="status">
            Account deleted.
          </div>
        )}

        <div className="account-page__actions">
          <button type="button" className="account-page__deleteBtn" onClick={doDelete} disabled={!canDelete}>
            {busy ? 'Deleting…' : 'Delete account'}
          </button>
          <button type="button" className="account-page__secondaryBtn" onClick={logout} disabled={busy}>
            Log out
          </button>
        </div>
      </section>
    </main>
  );
}

